import React, { useCallback, useEffect } from 'react';
import { isFirefox } from 'react-dnd-html5-backend/lib/BrowserDetector';
import { useMedia } from 'react-use';

import { breakpoints } from 'theme';

const useGridScroll = ({
  containerRef,
  stickyCellWidth,
  zoom,
  isMenuOpen,
  onScrollStartReached,
  isLoading,
  hasNextPage,
  hasPreviousPage,
}: {
  containerRef: React.RefObject<HTMLElement>;
  stickyCellWidth: number;
  zoom: number;
  isMenuOpen: boolean;
  onScrollStartReached?: () => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}): {
  handleScrollGrid: (position: 'start' | 'end') => void | boolean;
  scrollCount: number;
} => {
  const isDownSmPlus = useMedia(breakpoints.downSmPlus);

  const getScrollCount = useCallback((numEvents: number) => {
    const floorEvents = Math.floor(numEvents);
    if (floorEvents <= 8) return 3;
    if (floorEvents <= 10) return 4;
    if (floorEvents <= 13) return 5;
    if (floorEvents <= 16) return 6;
    if (floorEvents <= 19) return 7;
    if (floorEvents <= 22) return 8;
    return 9;
  }, []);

  useEffect(() => {
    localStorage.removeItem('differencesBoxArea');
    localStorage.removeItem('differencesBoxAreaStart');
  }, [zoom, isMenuOpen, isDownSmPlus]);

  const handleScrollGrid = useCallback(
    (positionToScroll: 'start' | 'end') => {
      const storageKey =
        positionToScroll === 'end'
          ? 'differencesBoxArea'
          : 'differencesBoxAreaStart';
      const otherStorageKey =
        positionToScroll === 'end'
          ? 'differencesBoxAreaStart'
          : 'differencesBoxArea';

      const existingDifferencesBoxArea = localStorage.getItem(storageKey);
      const scrollLeft = containerRef.current.scrollLeft;
      const scrollWidth = containerRef.current.scrollWidth;
      const clientWidth = containerRef.current.getBoundingClientRect().width;

      const eventBoxWidth = Math.floor(157 * zoom);
      const numberOfEventsInBoxArea =
        (clientWidth - stickyCellWidth * zoom) / eventBoxWidth;
      const scrollCountValue = getScrollCount(numberOfEventsInBoxArea);
      const differencesBoxArea = existingDifferencesBoxArea
        ? 0
        : numberOfEventsInBoxArea - Math.floor(numberOfEventsInBoxArea);

      localStorage.setItem(
        storageKey,
        (Math.round(differencesBoxArea * 100) / 100).toString(),
      );
      localStorage.removeItem(otherStorageKey);

      const scrollOffset =
        (scrollCountValue - Math.round(differencesBoxArea * 100) / 100) *
        (eventBoxWidth +
          (numberOfEventsInBoxArea - Math.floor(numberOfEventsInBoxArea)) *
            1.25);

      const newScrollLeft =
        positionToScroll === 'end'
          ? scrollLeft + scrollOffset
          : scrollLeft - scrollOffset;

      if (isLoading) {
        containerRef.current.scroll({
          top: 0,
          left: newScrollLeft,
          behavior: isFirefox() ? 'auto' : 'smooth',
        });
        return true;
      }

      if (
        scrollLeft <= 0 &&
        newScrollLeft <= 0 &&
        positionToScroll === 'start'
      ) {
        if (onScrollStartReached && hasPreviousPage) {
          onScrollStartReached();
          return true;
        }
        return false;
      }

      if (
        scrollLeft + clientWidth >= scrollWidth &&
        newScrollLeft >= scrollLeft &&
        positionToScroll === 'end'
      ) {
        if (hasNextPage) {
          return true;
        }
        return false;
      }

      containerRef.current.scroll({
        top: 0,
        left: newScrollLeft,
        behavior: isFirefox() ? 'auto' : 'smooth',
      });

      return true;
    },
    [
      containerRef,
      zoom,
      isMenuOpen,
      isDownSmPlus,
      onScrollStartReached,
      getScrollCount,
      stickyCellWidth,
      isLoading,
      hasNextPage,
      hasPreviousPage,
    ],
  );

  const eventBoxWidth = Math.floor(157 * zoom);
  const numberOfEventsInBoxArea = containerRef.current
    ? (containerRef.current.getBoundingClientRect().width -
        stickyCellWidth * zoom) /
      eventBoxWidth
    : 0;

  return {
    handleScrollGrid,
    scrollCount: getScrollCount(numberOfEventsInBoxArea),
  };
};

export default useGridScroll;
